import React, { useState, useRef, useEffect, useCallback } from "react";
import { Spinner } from "react-bootstrap";

const API_URL = process.env.REACT_APP_API_URL;

// =============================================================================
// AddressAutocompleteField — Google-backed address autocomplete.
//   GET {searchUrl}?input=&country=        → predictions
//   GET {detailsUrl}/{placeId}             → full address details
// On pick, the details object is handed to onSelectDetails so the parent can
// fan it out to City / State / Zip / Country fields.
//
// Styling-agnostic: pass `className` for the input (defaults to "form-control"
// for bootstrap forms; pass "qw-input" for the quote/customer design).
//
// Props:
//   config: { searchUrl, detailsUrl, country?, minChars? }
//   value, onChange(text), onSelectDetails(details)
//   className, placeholder, disabled, required, style
// =============================================================================
export default function AddressAutocompleteField({
  config, value, onChange, onSelectDetails,
  className = "form-control", placeholder, disabled, required, style,
}) {
  const [inputValue, setInputValue] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => { setInputValue(value || ""); }, [value]);

  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const fetchSuggestions = useCallback((query) => {
    const minChars = config.minChars ?? 3;
    if (!query || query.length < minChars) { setSuggestions([]); setIsOpen(false); return; }
    setIsLoading(true);
    const country = config.country || "US";
    const url = `${API_URL}${config.searchUrl}?input=${encodeURIComponent(query)}&country=${encodeURIComponent(country)}`;
    fetch(url)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((data) => {
        const items = data?.predictions || [];
        setSuggestions(items); setIsOpen(items.length > 0); setHighlightIdx(-1);
      })
      .catch(() => { setSuggestions([]); setIsOpen(false); })
      .finally(() => setIsLoading(false));
  }, [config]);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setInputValue(val); onChange(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 300);
  };

  const handleSelect = async (item) => {
    setIsOpen(false);
    if (!item?.placeId) return;
    try {
      const base = (config.detailsUrl || "").replace(/\/$/, "");
      const r = await fetch(`${API_URL}${base}/${encodeURIComponent(item.placeId)}`);
      if (!r.ok) throw new Error(r.status);
      const details = await r.json();
      const head = details?.address1 || item.mainText || "";
      setInputValue(head); onChange(head); onSelectDetails?.(details);
    } catch (err) { console.error("Place details fetch failed:", err); }
  };

  const handleKeyDown = (e) => {
    if (!isOpen) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlightIdx((p) => Math.min(p + 1, suggestions.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlightIdx((p) => Math.max(p - 1, 0)); }
    else if (e.key === "Enter" && highlightIdx >= 0) { e.preventDefault(); handleSelect(suggestions[highlightIdx]); }
    // UI-CUS-097: mark Esc as handled so a surrounding modal stays open; a second Esc closes it.
    else if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); setIsOpen(false); }
  };

  return (
    <div ref={wrapperRef} style={{ position: "relative" }}>
      <input
        type="text"
        className={className}
        style={style}
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => { if (suggestions.length > 0) setIsOpen(true); }}
        disabled={disabled}
        placeholder={placeholder}
        required={required}
        autoComplete="off"
      />
      {isLoading && (
        <div style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}>
          <Spinner animation="border" size="sm" style={{ width: 14, height: 14 }} />
        </div>
      )}
      {isOpen && suggestions.length > 0 && (
        <div style={{
          position: "absolute", top: "100%", left: 0, right: 0, zIndex: 2000,
          background: "#fff", border: "1px solid #dee2e6", borderRadius: "0 0 6px 6px",
          maxHeight: 240, overflowY: "auto", boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        }}>
          {suggestions.map((item, idx) => (
            <div
              key={item.placeId || idx}
              onClick={() => handleSelect(item)}
              onMouseEnter={() => setHighlightIdx(idx)}
              style={{
                padding: "8px 12px", cursor: "pointer", fontSize: 13,
                backgroundColor: idx === highlightIdx ? "#e8f0fe" : "#fff",
                borderBottom: idx < suggestions.length - 1 ? "1px solid #f0f0f0" : "none",
              }}
            >
              <div style={{ fontWeight: 600, color: "#0052A5" }}>{item.mainText}</div>
              <div style={{ color: "#888", fontSize: 12 }}>{item.secondaryText}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
